import { observer, inject } from 'mobx-react';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Button, Card, Checkbox, Icon, Intent, Label, Spinner, TextArea } from '@blueprintjs/core';
import { TLCode } from '../../common/enums';
import DutySelection from './DutySelection';
import styled from '@emotion/styled';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Instance } from 'mobx-state-tree';
import { PharmacyDetails, PharmacyDetails as PharmacyDetailsEx } from '../../store/models/PharmacyDetails';
import { IconNames } from '@blueprintjs/icons';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { SystemStatus } from '../../store/models/SystemStatus';
import PharmacySelection from '../common/dropdownSelection/PharmacySelection';
import LeaveRouteDirtyWrapper from '../common/LeaveRouteDirtyWrapper';
import TranslatedMessage from '../common/translation/TranslatedMessage';

import { AuthenticationStore } from '../../store';

interface NewExchangeTabProps extends WithTranslateProps {
  duties?: Array<Instance<typeof DutyAssignment>>;
  neighborDuties?: Array<Instance<typeof DutyAssignment>>;
  onExchangeRequesterSelected: (value) => void;
  onExchangeNeighborSelected: (value) => void;
  onSave: () => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  pharmacyNeighboringList: Array<Instance<typeof PharmacyDetailsEx>>;
  pharmacyRequesterSelected?: Instance<typeof PharmacyDetails>;
  pharmacyNeighborSelected?: Instance<typeof PharmacyDetails>;
  onRequesterDutiesSelected: (value) => void;
  onNeighborDutiesSelected: (value) => void;
  isInConflict: (dutyAssignment, targetPharmacy) => boolean;
  hasAlreadyDutyOn: (dutyAssignment, targetPharmacy) => boolean;
  getPharmacyById: (id) => any;
  pharmacyUser: boolean;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  requesterDutiesSelected: number[];
  neighborDutiesSelected: number[];
  withAgreement?: boolean;
  withoutSubstitution?: boolean;
  comment?: string;
  onWithoutSubstitutionChange: () => void;
  onWithAgreementChange: () => void;
  onCommentChange: (value) => void;
  requestorDutiesIsLoading?: boolean;
  neighborDutiesIsLoading?: boolean;
  exchangeIsSaving?: boolean;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface NewExchangeTabState {
  notification: string;
  showWithoutSubstitution: boolean;
}

@inject('AuthenticationStore')
@observer
class NewExchangeTab extends React.Component<NewExchangeTabProps, NewExchangeTabState> {
  state = {
    notification: '',
    showWithoutSubstitution: true,
  } as NewExchangeTabState;

  getIsDirty = () => {
    const { requesterDutiesSelected, neighborDutiesSelected } = this.props;

    return requesterDutiesSelected.length > 0 || neighborDutiesSelected.length > 0;
  };

  somethingToSave = () => {
    const {
      requesterDutiesSelected,
      neighborDutiesSelected,
      withoutSubstitution,
      withAgreement,
      pharmacyUser,
    } = this.props;
    const ready =
      (!pharmacyUser || withAgreement) &&
      (requesterDutiesSelected.length > 0 || neighborDutiesSelected.length > 0) &&
      (neighborDutiesSelected.length > 0 || withoutSubstitution);
    return ready;
  };

  requesterDutySelected = (data) => {
    this.props.onRequesterDutiesSelected(data);
  };

  recipientDutySelected = (data) => {
    if (this.props.withoutSubstitution) {
      this.props.onWithoutSubstitutionChange();
    }
    this.setState({ showWithoutSubstitution: data.length == 0 });
    this.props.onNeighborDutiesSelected(data);
  };

  isRequesterInConflict = (dutyAssignmentId) => {
    if (this.props.pharmacyNeighborSelected)
      return this.props.isInConflict(dutyAssignmentId, this.props.pharmacyNeighborSelected!.pharmacyId);
    return false;
  };

  hasRequesterAlreadyDutyOn = (dutyAssignmentId) => {
    if (this.props.pharmacyNeighborSelected)
      return this.props.hasAlreadyDutyOn(dutyAssignmentId, this.props.pharmacyNeighborSelected!.pharmacyId);
    return false;
  };

  isRecipientInConflict = (dutyAssignmentId) => {
    if (this.props.pharmacyRequesterSelected)
      return this.props.isInConflict(dutyAssignmentId, this.props.pharmacyRequesterSelected!.pharmacyId);
    return false;
  };

  hasRecipientAlreadyDutyOn = (dutyAssignmentId) => {
    if (this.props.pharmacyRequesterSelected)
      return this.props.hasAlreadyDutyOn(dutyAssignmentId, this.props.pharmacyRequesterSelected!.pharmacyId);
    return false;
  };

  public render() {
    const {
      duties,
      neighborDuties,
      translate,
      onExchangeRequesterSelected,
      onExchangeNeighborSelected,
      onSave,
      pharmacyList,
      pharmacyNeighboringList,
      pharmacyRequesterSelected,
      pharmacyNeighborSelected,
      onRequesterDutiesSelected,
      getPharmacyById,
      pharmacyUser,
      systemStatus,
      neighborDutiesSelected,
      requesterDutiesSelected,
      requestorDutiesIsLoading,
      neighborDutiesIsLoading,
      exchangeIsSaving,
    } = this.props;
    const dirty = this.getIsDirty();
    const somethingToSave = this.somethingToSave();

    return (
      <Container>
        <LeaveRouteDirtyWrapper blockChange={dirty} />
        {exchangeIsSaving ? (
          <SpinnerContainer>
            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
          </SpinnerContainer>
        ) : (
          <>
            <SelectionContainer>
              <PharmacyContainer>
                <Card title={translate(TLCode.EXCHANGE_PHARMACY_REQUESTER_CARD_TITLE)} style={{ marginBottom: 10 }}>
                  <PharmacySelection
                    pharmacyList={pharmacyList}
                    onChange={onExchangeRequesterSelected}
                    pharmacySelected={pharmacyRequesterSelected}
                    getPharmacyById={getPharmacyById}
                    disabled={pharmacyUser}
                    showDetails={true}
                    colored={false}
                  />
                </Card>
                {pharmacyRequesterSelected || requestorDutiesIsLoading ? (
                  <Card
                    title={translate(TLCode.EXCHANGE_DUTIES_CARD_TITLE)}
                    style={{ marginBottom: 10, height: '300px', overflowY: 'auto' }}
                  >
                    {requestorDutiesIsLoading ? (
                      <SpinnerContainer>
                        <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
                      </SpinnerContainer>
                    ) : (
                      <DutySelection
                        dutyList={duties!}
                        onChange={onRequesterDutiesSelected}
                        systemStatus={systemStatus}
                        pharmacyUser={pharmacyUser}
                        selectedDuties={requesterDutiesSelected}
                        isInConflict={this.isRequesterInConflict}
                        hasAlreadyDutyOn={this.hasRequesterAlreadyDutyOn}
                      />
                    )}
                  </Card>
                ) : null}
              </PharmacyContainer>
              <IconContainer>
                <div>
                  <Icon icon={IconNames.DOUBLE_CHEVRON_LEFT} iconSize={80} />
                  <Icon icon={IconNames.DOUBLE_CHEVRON_RIGHT} iconSize={80} />
                </div>
              </IconContainer>
              <PharmacyContainer>
                {pharmacyRequesterSelected ? (
                  <div>
                    <Card title={translate(TLCode.EXCHANGE_PHARMACY_OTHER_CARD_TITLE)} style={{ marginBottom: 10 }}>
                      <DivLine>
                        <DivLeftColumn>
                          <PharmacySelection
                            pharmacyList={pharmacyNeighboringList.filter(
                              (p) => pharmacyRequesterSelected!.level > 0 && p.level > 0,
                            )}
                            onChange={onExchangeNeighborSelected}
                            pharmacySelected={pharmacyNeighborSelected}
                            showDetails={true}
                            showLevel={false}
                            showDistance={true}
                            colored={false}
                          />
                        </DivLeftColumn>
                        <DivRightColumn>
                          <p>
                            <TranslatedMessage id={TLCode.EXCHANGE_DUTIES_OutsideList} />
                          </p>
                          <p style={{ color: 'red' }}>
                            <TranslatedMessage id={TLCode.EXCHANGE_DUTIES_AsTheCrowFlies} />
                          </p>
                        </DivRightColumn>
                      </DivLine>
                    </Card>
                    {pharmacyNeighborSelected || neighborDutiesIsLoading ? (
                      <Card
                        title={translate(TLCode.EXCHANGE_DUTIES_CARD_TITLE)}
                        style={{ marginBottom: 10, height: '300px', overflowY: 'auto' }}
                      >
                        {neighborDutiesIsLoading ? (
                          <SpinnerContainer>
                            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
                          </SpinnerContainer>
                        ) : (
                          <DutySelection
                            dutyList={neighborDuties!}
                            onChange={(data) => this.recipientDutySelected(data)}
                            systemStatus={systemStatus}
                            pharmacyUser={pharmacyUser}
                            disable={this.props.withoutSubstitution}
                            selectedDuties={neighborDutiesSelected}
                            isInConflict={this.isRecipientInConflict}
                            hasAlreadyDutyOn={this.hasRecipientAlreadyDutyOn}
                          />
                        )}
                      </Card>
                    ) : null}
                    {/* <Checkbox onChange={this.props.onWithoutSubstitutionChange} checked={this.props.withoutSubstitution}>
                  {translate(TLCode.EXCHANGE_WithoutSubstitutionMessage)}
                </Checkbox> */}
                  </div>
                ) : null}
              </PharmacyContainer>
            </SelectionContainer>
            <FormContainer>
              <NoReturnDiv>
                {this.state.showWithoutSubstitution && (
                  <Checkbox onChange={this.props.onWithoutSubstitutionChange} checked={this.props.withoutSubstitution}>
                    {translate(TLCode.EXCHANGE_WithoutSubstitutionMessage)}
                  </Checkbox>
                )}
              </NoReturnDiv>
              <AgreementDiv>
                <Checkbox onChange={this.props.onWithAgreementChange} checked={this.props.withAgreement}>
                  {translate(TLCode.EXCHANGE_WithAgreementMessage)}
                </Checkbox>
              </AgreementDiv>
              <br />
              <RemarkDiv>
                <Label style={{ marginRight: '10px' }}>{translate(TLCode.EXCHANGE_Remark)}</Label>
                <TextArea
                  fill={true}
                  value={this.props.comment}
                  onChange={(value) => this.props.onCommentChange(value.target.value)}
                />
              </RemarkDiv>
            </FormContainer>
            <ButtonsContainer>
              {somethingToSave && (
                <Button text={translate(TLCode.EXCHANGE_Save)} intent={Intent.PRIMARY} onClick={() => onSave()} />
              )}
            </ButtonsContainer>
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(NewExchangeTab);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SelectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 10px;
`;

const PharmacyContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 0.4;
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  flex: 0.2;
  vertical-align: middle;
  justify-content: center;
  align-items: center;
`;

const RemarkDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 60%;
`;

const NoReturnDiv = styled.div`
  margin-top: 10px;
`;

const AgreementDiv = styled.div`
  margin-top: 10px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
  width: 40%;
`;

const DivRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
  width: 60%;
`;
